import { useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { Mail, Send, Check, ArrowLeft } from "lucide-react";
import SEOHead from "../components/SEOHead";
import StoreSwitcher from "../components/StoreSwitcher";
import { api } from "../lib/api";

const STORE_ALIAS = {
    queens: "queens-long-island",
    "staten-island": "staten-island",
};

function ContactForm({ storeLabel }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const [ok, setOk] = useState(false);

    const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErr(null);
        try {
            await api.post("/aiq/contact", {
                ...form,
                marketing_consent: true,
                tags: [`zenzest-${storeLabel}-contact`],
            });
            setOk(true);
        } catch (e2) {
            setErr(e2?.response?.data?.detail || "Couldn't send right now. Try again.");
        } finally {
            setLoading(false);
        }
    };

    if (ok) {
        return (
            <div
                data-testid="contact-success"
                className="border border-zen-olive/30 bg-zen-olive/5 p-8 text-center"
            >
                <Check size={28} className="text-zen-olive mx-auto mb-4" />
                <h3 className="font-serif font-light text-2xl text-zen-ink mb-2 tracking-tight">
                    Got it — thanks.
                </h3>
                <p className="text-sm text-zen-ink-soft">
                    A real human at ZenZest will reach out within one business day.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft mb-2">
                        Name
                    </label>
                    <input
                        required
                        type="text"
                        value={form.name}
                        onChange={setField("name")}
                        data-testid="contact-name"
                        className="w-full bg-zen-bg border border-zen-line px-4 py-3 text-base text-zen-ink focus:outline-none focus:border-zen-olive focus:ring-2 focus:ring-zen-olive/15"
                    />
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft mb-2">
                        Email
                    </label>
                    <input
                        required
                        type="email"
                        value={form.email}
                        onChange={setField("email")}
                        data-testid="contact-email"
                        className="w-full bg-zen-bg border border-zen-line px-4 py-3 text-base text-zen-ink focus:outline-none focus:border-zen-olive focus:ring-2 focus:ring-zen-olive/15"
                    />
                </div>
            </div>
            <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft mb-2">
                    Phone (optional)
                </label>
                <input
                    type="tel"
                    inputMode="tel"
                    value={form.phone}
                    onChange={setField("phone")}
                    data-testid="contact-phone"
                    className="w-full bg-zen-bg border border-zen-line px-4 py-3 text-base text-zen-ink focus:outline-none focus:border-zen-olive focus:ring-2 focus:ring-zen-olive/15"
                />
            </div>
            <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft mb-2">
                    How can we help?
                </label>
                <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={setField("message")}
                    data-testid="contact-message"
                    className="w-full bg-zen-bg border border-zen-line px-4 py-3 text-base text-zen-ink focus:outline-none focus:border-zen-olive focus:ring-2 focus:ring-zen-olive/15 resize-none"
                />
            </div>
            {err && (
                <p data-testid="contact-error" className="text-sm text-red-700">
                    {err}
                </p>
            )}
            <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="w-full sm:w-auto bg-zen-ink text-zen-cream px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-olive-dark transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
            >
                {loading ? "Sending…" : "Send message"}
                {!loading && <Send size={13} />}
            </button>
        </form>
    );
}

export function NewsletterForm({ testid = "newsletter", compact = false }) {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [ok, setOk] = useState(false);
    const [err, setErr] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErr(null);
        try {
            await api.post("/aiq/newsletter", { email });
            setOk(true);
            setEmail("");
        } catch (e2) {
            setErr(e2?.response?.data?.detail || "Couldn't sign up — please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (ok) {
        return (
            <p
                data-testid={`${testid}-success`}
                className="text-sm text-zen-olive inline-flex items-center gap-2"
            >
                <Check size={14} /> Subscribed. Watch your inbox.
            </p>
        );
    }

    return (
        <form
            onSubmit={submit}
            className={`flex ${compact ? "flex-col sm:flex-row" : "flex-col sm:flex-row"} gap-3 w-full max-w-md`}
            data-testid={testid}
        >
            <input
                required
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid={`${testid}-email`}
                className="flex-1 bg-zen-bg border border-zen-line px-4 py-3 text-sm text-zen-ink focus:outline-none focus:border-zen-olive focus:ring-2 focus:ring-zen-olive/15"
            />
            <button
                type="submit"
                disabled={loading}
                data-testid={`${testid}-submit`}
                className="bg-zen-ink text-zen-cream px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-olive-dark transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
            >
                {loading ? "…" : "Subscribe"}
                {!loading && <Mail size={12} />}
            </button>
            {err && (
                <span
                    data-testid={`${testid}-error`}
                    className="text-xs text-red-700 self-center"
                >
                    {err}
                </span>
            )}
        </form>
    );
}

export default function StoreContact() {
    const { pathname } = useLocation();
    const storePrefix = pathname.split("/").filter(Boolean)[0];
    const storeSlug = STORE_ALIAS[storePrefix];
    if (!storeSlug) return <Navigate to="/" replace />;

    const storeName =
        storePrefix === "queens" ? "ZenZest Queens & Long Island" : "ZenZest Staten Island";

    return (
        <>
            <SEOHead
                title={`Contact ${storeName} — Cannabis Questions, Orders, Press`}
                description={`Reach the ${storeName} team. Product questions, custom orders, press, and partnerships. We reply within one business day.`}
                canonical={`https://weeddispensarynearme.com/${storePrefix}/contact`}
            />

            <section className="bg-zen-bg">
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-10 sm:py-14">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <Link
                            to={`/${storePrefix}`}
                            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-zen-ink-soft hover:text-zen-ink"
                        >
                            <ArrowLeft size={12} /> {storeName}
                        </Link>
                        <StoreSwitcher currentSlug={storeSlug} tone="dark" />
                    </div>
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                        Contact
                    </p>
                    <h1 className="font-serif font-light text-4xl sm:text-5xl text-zen-ink leading-[1.05] tracking-tight max-w-3xl mb-7">
                        Talk to a real person.
                    </h1>
                    <p className="text-base text-zen-ink-soft max-w-2xl mb-6 leading-relaxed">
                        Product questions, custom orders, press, partnerships — we&rsquo;ll get
                        back to you within one business day. For loyalty questions, the fastest
                        path is to{" "}
                        <Link
                            to="/loyalty"
                            className="text-zen-olive underline underline-offset-4 hover:text-zen-olive-dark"
                        >
                            check your points here
                        </Link>
                        .
                    </p>
                </div>
            </section>

            <section className="bg-zen-surface border-y border-zen-line">
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-14 grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-7">
                        <h2 className="font-serif font-light text-2xl text-zen-ink mb-6 tracking-tight">
                            Send us a message
                        </h2>
                        <ContactForm storeLabel={storePrefix} />
                    </div>
                    <div className="lg:col-span-5">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                            Stay in the loop
                        </p>
                        <h2 className="font-serif font-light text-2xl text-zen-ink mb-4 tracking-tight">
                            Friday drops & new strains, by email.
                        </h2>
                        <p className="text-sm text-zen-ink-soft mb-5 leading-relaxed">
                            Editorial-only. One email a week. Easy to unsubscribe.
                        </p>
                        <NewsletterForm testid="newsletter-page" />
                    </div>
                </div>
            </section>
        </>
    );
}
